import { Box } from '@mui/material';
import { ConfirmationDialog, PoolFormattedMessage, ValueOrNDash } from '@synergy/components';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { LocalizedString } from '@synergy/models';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { PoolLineClampTypography } from '@synergy/components-next';
import { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as TaxRatesActions from './store/tax-rates-actions';
import { TaxRate } from '../../../../common/models/tax-rate';
import { TranslationPill } from '../../components/translation-pill';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { taxRatesSelector } from './store/tax-rates-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface TaxRatesOverviewProps {
    onEdit: (unit: TaxRate) => void;
}

export const TaxRatesOverview = (props: TaxRatesOverviewProps) => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const taxRates = useAppSelector(taxRatesSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams({ sort: 'id', order: 'asc' }));
    const [deleteModalData, setDeleteModalData] = useState<TaxRate | null>(null);

    // The list sorts on the server, so the chosen column travels in the address.
    // Until one is chosen the list claims no order of its own — an arrow over a
    // column the server is not sorting by says something untrue.
    const sortColumn = searchParams.get('sort');
    const sort = sortColumn
        ? { key: sortColumn, order: ('desc' === searchParams.get('order') ? 'desc' : 'asc') as 'asc' | 'desc' }
        : undefined;

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    /** The chosen language, or the reader's own marked as the stand-in. */
    const translation = (value?: LocalizedString | null): ReactNode =>
        value
            ? value[selectedLanguage] ||
              (value[currentLocale] && (
                  <Box color="grey.700">
                      {currentLocale.toUpperCase()}: {value[currentLocale]}
                  </Box>
              ))
            : null;

    const columns: PaloColumn<TaxRate>[] = [
        {
            key: 'id',
            type: 'documentNumber',
            sortKey: 'id',
            head: <FormattedMessage id="app.common.id" />,
            cell: taxRate => taxRate.id,
        },
        {
            key: 'counterAccount',
            type: 'classification',
            sortKey: 'counterAccount',
            head: <FormattedMessage id="app.common.counterAccount" />,
            cell: taxRate => <ValueOrNDash value={taxRate.counterAccount} strictEqual={true} />,
        },
        {
            key: 'taxCode',
            type: 'classification',
            sortKey: 'taxCode',
            head: <FormattedMessage id="app.common.taxCode" />,
            cell: taxRate => <ValueOrNDash value={taxRate.taxCode} strictEqual={true} />,
        },
        {
            key: 'taxPercentage',
            type: 'percent',
            head: <FormattedMessage id="app.configuration.taxRates.taxRate" />,
            cell: taxRate => (
                <ValueOrNDash
                    value={taxRate.taxPercentage}
                    strictEqual={true}
                    valueRender={taxPercentage => (
                        <FormattedNumber maximumFractionDigits={2} value={taxPercentage / 100} style="percent" />
                    )}
                />
            ),
        },
        {
            key: 'branchNumber',
            type: 'classification',
            head: <FormattedMessage id="app.configuration.taxRates.branchNumber" />,
            cell: taxRate => <ValueOrNDash value={taxRate.branchNumber} strictEqual={true} />,
        },
        {
            key: 'description',
            type: 'name',
            head: <FormattedMessage id="app.table.column.description" />,
            cell: taxRate => translation(taxRate.description),
        },
        {
            key: 'invoiceText',
            type: 'name',
            head: <FormattedMessage id="app.configuration.taxRates.invoiceText" />,
            cell: taxRate => (
                <PoolLineClampTypography maxLines={2}>
                    <ValueOrNDash>{translation(taxRate.invoiceText)}</ValueOrNDash>
                </PoolLineClampTypography>
            ),
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: item => <TranslationPill value={item.description} locales={availableLocales} />,
        },
    ];

    useEffect(() => {
        dispatch(TaxRatesActions.fetchTaxRates(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(TaxRatesActions.deleteTaxRate(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<TaxRate, number>
                embedded={true}
                columns={columns}
                items={taxRates.items}
                getRowId={taxRate => taxRate.id}
                emptyLabel="Keine Steuersätze."
                rowActions={taxRate => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(taxRate) },
                    { label: <FormattedMessage id="app.common.delete" />, onClick: () => setDeleteModalData(taxRate) },
                ]}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                pagination={{
                    count: taxRates.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.taxRates.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage id="app.configuration.taxRates.delete.text" />
            </ConfirmationDialog>
        </>
    );
};
